import type { ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import BrandSwitcher from './BrandSwitcher'
import { useState, useEffect, useRef } from 'react'
import { usePreserveQueryParams, useDemoMode } from '../hooks/usePreserveQueryParams'
import { demoBrandPerformance } from '../utils/demoData'
import {
  ChartNoAxesCombined,
  FileText,
  Crosshair,
  Settings,
  LogOut,
  TrendingUp,
  User,
  LucideBookOpenText,
  Brain,
  ChevronDown,
  Mail,
  Menu,
  X,
  ShieldCheck,
  BarChart3,
  EyeOff
} from 'lucide-react'

interface LayoutProps {
  children: ReactNode
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const { currentUser, activeBrand, logout, impersonation, stopImpersonation } = useAuth()
  const { withParams } = usePreserveQueryParams()
  const { isDemoMode } = useDemoMode()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [showMobileHeader, setShowMobileHeader] = useState(true)
  const lastScrollY = useRef(0)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Use AI score directly from activeBrand (same source as BrandPerformanceCard)
  // In demo mode, use the demo score
  const aiScore = isDemoMode ? demoBrandPerformance.aiIndexScore : (activeBrand?.aiIndexScore || 0)

  // Scroll direction detection for mobile header
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY

      if (currentScrollY > lastScrollY.current && currentScrollY > 80) {
        // Scrolling down & past threshold - hide
        setShowMobileHeader(false)
      } else if (currentScrollY < lastScrollY.current || currentScrollY < 10) {
        // Scrolling up OR at top - show
        setShowMobileHeader(true)
      }

      lastScrollY.current = currentScrollY
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close mobile menu when location changes
  useEffect(() => {
    setIsMobileMenuOpen(false)
  }, [location.pathname])

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  // Build navigation array, including Admin link if user is admin
  const navigation = [
    { name: 'Brand Analysis', href: '/dashboard', icon: ChartNoAxesCombined },
    { name: 'Search Intelligence', href: '/search', icon: Brain },
    { name: 'Market Intelligence', href: '/market', icon: BarChart3 },
    { name: 'Competitors', href: '/competition', icon: Crosshair },
    { name: 'Content Manager', href: '/content', icon: LucideBookOpenText },
    { name: 'LLMS.txt Generator', href: '/llms', icon: FileText }
  ]

  return (
      <>
        {/* Impersonation Banner */}
        {impersonation?.isImpersonating && (
            <div className="bg-amber-500 text-white px-4 py-2 text-center text-sm font-medium shadow-lg sticky top-0 z-50">
              <div className="flex items-center justify-center gap-4">
                <span className="inline-flex items-center gap-2">
                  <EyeOff className="h-4 w-4" />
                  <span>
                    Impersonating as <strong>{currentUser?.email}</strong>
                    {impersonation.reason && <span className="opacity-75"> ({impersonation.reason})</span>}
                  </span>
                </span>
                <button
                    onClick={stopImpersonation}
                    className="px-3 py-1 bg-white text-amber-600 rounded-md hover:bg-amber-50 transition-colors font-semibold text-xs"
                >
                  Stop Impersonating
                </button>
              </div>
            </div>
        )}

        {/* Demo Mode Banner */}
        {isDemoMode && (
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-4 py-3 text-center text-sm font-medium shadow-lg sticky top-0 z-50">
              <div className="flex items-center justify-center">
            <span className="inline-flex items-center gap-2">
              <span>DEMO MODE</span>
            </span>
                <a
                    href="/register"
                    className="ml-4 px-3 py-1 bg-white text-indigo-600 rounded-md hover:bg-indigo-50 transition-colors inline-block font-semibold text-xs"
                >
                  Sign up to track your brand →
                </a>
              </div>

            </div>
        )}

    <div className="relative min-h-screen bg-gray-50 isolate">

      {/* Mobile Header - Only visible on mobile */}
      <div
        className={`
          lg:hidden bg-white border-b border-gray-200 px-4 py-3
          flex items-center gap-4 absolute top-0 left-0 right-0 z-40
          transition-transform duration-300 ease-in-out
          ${showMobileHeader ? 'translate-y-0' : '-translate-y-full'}
        `}
        style={{ boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}
      >
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-700" />
          ) : (
            <Menu className="h-6 w-6 text-gray-700" />
          )}
        </button>
        <div className="flex items-center space-x-2">
          <TrendingUp className="h-6 w-6 text-indigo-600" />
          <span className="text-lg font-bold">BrandIndex.ai</span>
        </div>
        <div className="w-10"></div> {/* Spacer to center the logo */}
      </div>

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      <div className="flex h-screen pt-[60px] lg:pt-0">
        {/* Sidebar */}
        <div className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-white shadow-lg
          transform transition-transform duration-300 ease-in-out
          lg:transform-none
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="flex flex-col h-full">
            <div className="p-6">
              {/* Logo */}
              <div className="flex items-center space-x-2 mb-4">
                <TrendingUp className="h-8 w-8 text-indigo-600" />
                <span className="text-xl font-bold">BrandIndex.ai</span>
              </div>

              {/* Brand Switcher */}
              <BrandSwitcher />

              {/* Active Brand Info */}
              {(activeBrand || isDemoMode) && (
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">AI Score</div>
                    <div className="space-x-1 flex items-center text-md font-semibold text-indigo-600">
                      <span>{aiScore.toFixed(1)}</span>
                      <span className="text-gray-400 text-sm font-normal">/ 100</span>
                    </div>
                  </div>
                </div>
              )}

            </div>

            <nav className="flex-1 px-4 pb-4">
              {navigation.map((item) => {
                const Icon = item.icon
                const isActive = location.pathname === item.href
                return (
                  <Link
                    key={item.name}
                    to={withParams(item.href)}
                    className={`
                      flex items-center px-4 py-3 mb-2 rounded-lg transition-colors
                      ${isActive
                        ? 'bg-indigo-50 text-indigo-600'
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon className="h-5 w-5 mr-3" />
                    <span className="font-medium">{item.name}</span>
                  </Link>
                )
              })}
            </nav>

            <div className="p-4 border-t" ref={dropdownRef}>
              {currentUser ? (
                <div className="relative">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="flex items-center justify-between w-full p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-gray-500" />
                      <span className="text-sm font-medium">{currentUser.firstName} {currentUser.lastName}</span>
                    </div>
                    <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute bottom-full left-0 right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
                      {currentUser.isAdmin && (
                        <Link
                            to={withParams('/admin')}
                            onClick={() => setIsDropdownOpen(false)}
                            className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          <ShieldCheck className="h-4 w-4" />
                          <span className="font-medium text-sm">Admin Panel</span>
                        </Link>
                      )}

                      <Link
                        to={withParams('/settings')}
                        onClick={() => setIsDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        <Settings className="h-4 w-4" />
                        <span className="font-medium text-sm">Settings</span>
                      </Link>
                      <Link
                        to={withParams('/contact')}
                        onClick={() => setIsDropdownOpen(false)}
                        className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
                      >
                        <Mail className="h-4 w-4" />
                        <span className="font-medium text-sm">Contact Us</span>
                      </Link>
                      <button
                        onClick={() => {
                          setIsDropdownOpen(false)
                          handleLogout()
                        }}
                        className="flex items-center gap-3 w-full px-4 py-3 text-gray-700 hover:bg-gray-50 transition-colors border-t border-gray-100"
                      >
                        <LogOut className="h-4 w-4" />
                        <span className="font-medium text-sm">Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/register"
                  className="flex items-center justify-center w-full px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg font-semibold text-sm hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md hover:shadow-lg"
                >
                  Create Account
                </Link>
              )}
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 overflow-auto">
          <main className="p-4 md:p-8">
            {children}
          </main>
        </div>
      </div>
    </div>
  </>
  )
}